import axios from 'axios'
import * as Sentry from '@sentry/react'

// ── API Base Configuration ──────────────────────────────────────────────
const API_BASE = (import.meta.env.VITE_API_URL ?? 'https://crm.kickoffadvertising.com/api')

export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  // axios >=1.6 only auto-attaches the XSRF header on same-origin requests
  // by default (a CSRF-token-leak hardening measure). Backend + frontend
  // are on different origins/ports whenever they're not served from behind
  // the same reverse proxy (local dev: :3001 vs :8000) - explicit opt-in
  // needed for any django_auth-protected mutation (POST/PATCH/DELETE) to
  // carry its CSRF token at all.
  withXSRFToken: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken'
})

// Response Interceptor: Handle Global Errors (like 401)
api.interceptors.response.use(
  r => {
    console.log(`✅ API Success: ${r.config.url}`, r.data);
    return r;
  },
  err => {
    const isExpected404 = err.response?.status === 404 && err.config?.url?.includes('/postback')
    if (!isExpected404) {
      console.error(`❌ API Error: ${err.config?.url}`, err.response?.data || err.message);
      if (err.response?.status !== 401) {
        Sentry.captureException(err)
      }
    }
    
    if (err.response?.status === 401) {
      console.warn("Sesión expirada o token inválido. Redirigiendo al login...");
      localStorage.removeItem('cen_user')
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    // A 403 can mean an impersonation session ended/expired server-side
    // (e.g. TTL, or another tab clicked "End session") while this tab's
    // banner still thinks it's active, OR it can mean the backend's
    // RequirePasswordChangeMiddleware just started blocking this session
    // (must_change_password flipped mid-session - an admin reset the
    // password, another tab changed it, etc). Both cases need the same
    // fix: re-fetch /auth/me so AppContext's user (and mustChangePassword)
    // reflects reality instead of stale client state. RequireAuth's
    // pathname check already makes this loop-safe once the redirect to
    // /force-password-change lands.
    if (err.response?.status === 403) {
      window.dispatchEvent(new Event('auth:scope-mismatch'))
    }

    return Promise.reject(err)
  }
)

export default api
